import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { DownloadOption } from 'types';
import songsApi from 'services/api/songs/songsApi';
import { isDownloadLimitError } from 'services/helpers/error';
import { appendDownloadLink } from 'services/hooks/download/helpers';
import useMessageSnackbar from 'services/hooks/useMessageSnackbar';

interface UseDownloadSong {
  songId: string;
  onSuccess: (downloadOption: DownloadOption) => void;
}

const useDownloadSong = ({ songId, onSuccess }: UseDownloadSong) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { displayErrorSnackbar } = useMessageSnackbar();

  const [downloadSong, { data, isLoading, error, originalArgs }] =
    songsApi.useDownloadSongMutation();

  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);

  useEffect(() => {
    if (!data || !originalArgs) {
      return;
    }

    const url = `${data.url}?file-name=${data.name}`;
    appendDownloadLink(url);
    onSuccess(originalArgs.downloadOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, originalArgs, dispatch]);

  useEffect(() => {
    if (!error) return;

    if (isDownloadLimitError(error)) {
      setIsLimitModalOpen(true);
      return;
    }

    displayErrorSnackbar(t('messages:error:unknown'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, error]);

  const handleSongDownload = useCallback(
    (downloadOption: DownloadOption) => {
      downloadSong({ songId, downloadOption });
    },
    [downloadSong, songId]
  );

  return {
    success: !!data,
    loading: isLoading,
    downloadSong: handleSongDownload,
    isLimitModalOpen,
    closeLimitModal: () => setIsLimitModalOpen(false),
  };
};

export default useDownloadSong;
